import { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { ExportFormat } from "../../constants/export-dropdown.constants";
import { useNotification } from "../../contexts/admin/NotificationContext";
import { handleGenericExport } from "../../utils/export";
import { useServerSidePagination } from "../shared/Pagination/usePagination";
import { useDebounce } from "./Other/useDebounce";
import { UseGenericManagementOptions, ServerValidationError } from "@/types/management.types";
import { useT } from "../../i18n";

import { tr } from '@/i18n';
export const useGenericManagement = <
  TEntity extends { id: number },
  TFilterParams
>(
  options: UseGenericManagementOptions<TEntity, TFilterParams>
) => {
  const {
    apiService,
    entityNameSingular,
    entityNamePlural,
    itemsPerPage: initialItemsPerPage,
    initialUiFilterValues,
    filterFieldsConfig = [],
    mapUiFiltersToApiParams,
  } = options;

  const { showNotification } = useNotification();
  const { t } = useT();

  const [items, setItems] = useState<TEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  const [uiFilterValues, setUiFilterValues] = useState<Record<string, string>>(
    initialUiFilterValues
  );
  const debouncedUiFilterValues = useDebounce(uiFilterValues, 1000);

  const [apiFilters, setApiFilters] = useState<TFilterParams>(() =>
    mapUiFiltersToApiParams(debouncedUiFilterValues)
  );

  const [totalItems, setTotalItems] = useState(0);
  const {
    currentPage,
    setCurrentPage,
    handlePreviousPage,
    handleNextPage,
    offset,
    goToPage,
    paginationRange,
  } = useServerSidePagination({ totalItems, itemsPerPage });

  const [showForm, setShowForm] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<TEntity | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const newApiFilters = mapUiFiltersToApiParams(debouncedUiFilterValues);
    if (JSON.stringify(newApiFilters) !== JSON.stringify(apiFilters)) {
      setCurrentPage(1);
      setApiFilters(newApiFilters);
    }
  }, [
    debouncedUiFilterValues,
    mapUiFiltersToApiParams,
    setCurrentPage,
    apiFilters,
  ]);
  
  useEffect(() => {
    if (currentPage > 1 && items.length === 0) {
      goToPage(1);
    }
  }, [itemsPerPage, currentPage, items.length, goToPage]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAll(
        offset,
        itemsPerPage,
        apiFilters
      );
      setItems(response.items);
      setTotalItems(response.total);
    } catch (err) {
      let message = tr("ui.failed_to_load_x", "Failed to load {v0}.", { v0: entityNamePlural });
      if (err instanceof AxiosError && err.response?.data) {
        const detail = err.response.data.detail;
        if (typeof detail === "string") {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail
            .map((e: ServerValidationError) => `${e.loc.join(".")} - ${e.msg}`)
            .join("; ");
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
      showNotification(message, "error");
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    apiService,
    offset,
    itemsPerPage,
    apiFilters,
    entityNamePlural,
    showNotification,
  ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const refetchItems = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  const handleUiFilterChange = useCallback(
    (name: string, value: string) => {
      const fieldConfig = filterFieldsConfig.find((f) => f.name === name);
      let processedValue = value;
      if (
        fieldConfig?.inputMode === "numeric" &&
        fieldConfig?.type !== "search"
      ) {
        processedValue = value.replace(/[^0-9]/g, "");
      }
      setUiFilterValues((prev) => ({ ...prev, [name]: processedValue }));
    },
    [filterFieldsConfig]
  );

  const clearAllFilters = useCallback(() => {
    setUiFilterValues(initialUiFilterValues);
  }, [initialUiFilterValues]);

  const handleFilterInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleUiFilterChange(e.target.name, e.target.value);
    },
    [handleUiFilterChange]
  );

  const handleSelectFilterChange = useCallback(
    (name: string, value: string) => {
      handleUiFilterChange(name, value);
    },
    [handleUiFilterChange]
  );

  const handleEdit = useCallback((item: TEntity) => {
    setItemToEdit(item);
    setShowForm(true);
  }, []);

  const handleShowAddForm = useCallback(() => {
    setItemToEdit(null);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    async (id: number) => {
      if (
        !window.confirm(
          tr("ui.are_you_sure_you_want_to_delete_this_x_this_migh_b31165", "Are you sure you want to delete this {v0}? This might affect related data.", { v0: entityNameSingular })
        )
      ) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        await apiService.deleteItem(id);
        showNotification(
          tr("ui.x_deleted_successfully", "{v0} deleted successfully!", { v0: entityNameSingular }),
          "success"
        );
        if (items.length === 1 && currentPage > 1) {
          setCurrentPage((prevPage) => prevPage - 1);
        } else {
          fetchItems();
        }
      } catch (err) {
        let message = tr("ui.failed_to_delete_x", "Failed to delete {v0}.", { v0: entityNameSingular });
        if (err instanceof AxiosError && err.response?.data) {
          const detail = err.response.data.detail;
          if (typeof detail === "string") {
            message = detail;
          } else if (typeof detail === "object" && detail !== null && "code" in detail) {
            const code = (detail as { code: string; args?: Record<string, string | number> }).code;
            const args = (detail as { code: string; args?: Record<string, string | number> }).args;
            const translated = t(code, args);
            message = translated !== code ? translated : JSON.stringify(detail);
          } else if (Array.isArray(detail)) {
            message = detail
              .map(
                (e: ServerValidationError) => `${e.loc.join(".")} - ${e.msg}`
              )
              .join("; ");
          }
        } else if (err instanceof Error) {
          message = err.message;
        }
        showNotification(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [
      apiService,
      entityNameSingular,
      items.length,
      currentPage,
      fetchItems,
      showNotification,
      setCurrentPage,
    ]
  );

  const handleFormSuccess = useCallback(() => {
    fetchItems();
    setShowForm(false);
    setItemToEdit(null);
  }, [fetchItems]);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (!apiService.exportItems) {
        showNotification(tr("ui.export_is_not_available_for_this_entity", "Export is not available for this entity."), "warning");
        return;
      }
      setIsExporting(true);
      try {
        await handleGenericExport({
          filters: apiFilters as Omit<TFilterParams, "offset" | "limit">,
          format,
          exportApiCall: (
            fmt: ExportFormat,
            fltrs: Omit<TFilterParams, "offset" | "limit"> | undefined
          ) => apiService.exportItems!(fmt, fltrs),
          fileNamePrefix: entityNamePlural.toLowerCase().replace(/\s+/g, "_"),
          setIsExporting,
          showNotification,
          entityName: entityNamePlural,
        });
      } finally {
      }
    },
    [apiService, apiFilters, entityNamePlural, showNotification]
  );

  return {
    items,
    loading: loading || isExporting,
    error,
    currentPage,
    setCurrentPage,
    totalItems,
    handlePreviousPage,
    handleNextPage,
    goToPage,
    paginationRange,
    uiFilterValues,
    setUiFilterValues: useCallback(setUiFilterValues, []),
    handleUiFilterChange,
    handleFilterInputChange,
    handleSelectFilterChange,
    clearAllFilters,
    handleEdit,
    handleShowAddForm,
    handleDelete,
    handleFormSuccess,
    showForm,
    setShowForm: useCallback(setShowForm, []),
    itemToEdit,
    handleExport,
    refetchItems,
    itemsPerPage,
    setItemsPerPage: useCallback(setItemsPerPage, []),
    showNotification,
    filterFields: filterFieldsConfig,
  };
};